import ApplicationController from '../application_controller'
import { Controller } from 'stimulus'
import { defaultPropertiesReport } from '../../constants/analytics'

export default class extends ApplicationController {
  static values = {
    report: Object,
    triggeredByModal: Boolean
  }

  connect() {
    this._reportCreateFilters = ['labels', 'issues', 'document_type', 'date',
      'location', 'priority', 'position', 'regulation_agencies',
      'bill_status']

    this._zeroDelay = 0

    const showPathRegex = new RegExp(/\/reports\/[0-9]/)

    if (!document.documentElement.hasAttribute('data-turbolinks-preview') && !this.triggeredByModalValue) {
      showPathRegex.test(window.location.pathname) ? this.reportViewed() : this.sendPageLoad()
    }
  }

  sendPageLoad() {
    const policyEditPathRegex = new RegExp(/\/reports\/policies\/[0-9]+\/edit/)
    const omniSearchEditPathRegex = new RegExp(/\/omni_searches\/[0-9]+\/edit/)
    let name = this.pathnameToEventName[window.location.pathname] || null
    const properties = { ...defaultPropertiesReport }

    if (policyEditPathRegex.test(window.location.pathname)) {
      name = 'Report Edit Builder Launched'
    } else if (omniSearchEditPathRegex.test(window.location.pathname)) {
      name = 'Report Builder Launched'
    }

    if (window.location.pathname.includes('policies') || window.location.pathname.includes('omni_searches')) {
      properties.report_type = 'Policy'
    } else if (window.location.pathname.includes('stakeholders')) {
      properties.report_type = 'Stakeholder'
    } else {
      properties.report_type = null
    }

    analytics.track(name, properties)
  }

  reportViewed() {
    analytics.track('Report Viewed', this.getReportProperties(this.reportValue))
  }

  stakeholderReportCreated(event) {
    const formElements = event.currentTarget.elements
    const submitUrl = event.currentTarget.action

    const properties = {
      ...defaultPropertiesReport,
      from_duplicate: false,
      report_components: this.getOptionsSelected(formElements) || null,
      report_format: 'PDF',
      report_id: null,
      report_size: [formElements.subject_ids.value].flat().length || null,
      report_type: submitUrl.split('/reports/')[1] || null,
      stakeholders_types: formElements.report_subtype.value || null
    }

    setTimeout(() => analytics.track('Report Created', properties), this._zeroDelay)
  }

  policyReportUpdated(event) {
    const formElements = event.currentTarget.elements
    const properties = this.getReportProperties(this.reportValue)
    properties.report_components = this.getOptionsSelected(formElements) || null
    properties.report_filter_values_selected = null
    properties.report_filters_applied = null

    setTimeout(() => analytics.track('Report Edited', properties), this._zeroDelay)
  }

  policyReportCreatedOrUpdated(event) {
    const reportId = event.detail.reportId
    const reportData = event.detail.reportData
    const selectedEntities = document.getElementById('results_selected_count').textContent
    const searchAttributes = reportData.search_attributes
    const filtersApplied = this.getFiltersApplied(this._reportCreateFilters, searchAttributes)

    const eventName = reportId ? 'Report Edited' : 'Report Created'
    const properties = {
      ...defaultPropertiesReport,
      from_duplicate: false,
      report_components: null,
      report_filter_values_selected: searchAttributes,
      report_filters_applied: filtersApplied,
      report_format: 'PDF',
      report_id: reportId,
      report_size: parseInt(selectedEntities.replace(',', '')) || null,
      report_type: 'Policy',
      stakeholders_types: null
    }

    analytics.track(eventName, properties)
  }

  policyReportScheduled(event) {
    const formElements = event.currentTarget.elements
    const formUrl = event.currentTarget.action
    const repeatEvery = []

    if (formElements.repeat_every_interval.value == 'week') {
      // Get repeats_every selected values in schedule report flyout
      const nodeElements = formElements['repeats_on[]']

      for (let i = 0; i < nodeElements.length; i++) {
        if (nodeElements[i].nodeName === 'INPUT' &&
          nodeElements[i].checked === true) {
          repeatEvery.push(nodeElements[i].value)
        }
      }
    }

    const properties = {
      schedule_id: formUrl.split('/schedules/')[1],
      email_recipients: formElements.recipient_emails.value,
      ends_on: formElements.end_date.value,
      repeats_every: repeatEvery,
      repeats_on: `${formElements.repeat_every.value} ${formElements.repeat_every_interval.value}`,
      send_time: formElements.send_time.value,
      start_date: formElements.start_date.value,
      time_zone: formElements.timezone.value
    }

    setTimeout(() => analytics.track('Policy Report Scheduled', properties), this._zeroDelay)
  }

  reportDuplicated(event) {
    const reportData = JSON.parse(event.currentTarget.dataset.analytics)
    const searchAttributes = reportData.search_attributes
    const filtersApplied = this.getFiltersApplied(this._reportCreateFilters, searchAttributes)

    const properties = {
      ...this.getReportProperties(reportData),
      report_filter_values_selected: searchAttributes,
      report_filters_applied: filtersApplied,
      from_duplicate: true
    }

    analytics.track('Report Created', properties)
  }

  reportEmbedded() {
    const embedDropdown = document.getElementById("select-embed-report")

    const properties = {
      emded_code_snippet_type: embedDropdown.selectedOptions[0].text,
    }

    analytics.track('Embed Report Link Copied', properties)
  }

  reportShared(event) {
    const reportData = JSON.parse(event.currentTarget.dataset.analytics)

    analytics.track('Report Shared', this.getReportProperties(reportData))
  }

  reportDeleted(event) {
    const reportData = JSON.parse(event.currentTarget.dataset.analytics)

    analytics.track('Report Deleted', this.getReportProperties(reportData))
  }

  reportDownloaded(event) {
    const reportData = JSON.parse(event.currentTarget.dataset.analytics)

    setTimeout(() => analytics.track('Report Downloaded', this.getReportProperties(reportData)), this._zeroDelay)
  }

  reportRefreshed(event) {
    const reportData = JSON.parse(event.currentTarget.dataset.analytics)

    analytics.track('Report Refreshed', this.getReportProperties(reportData))
  }

  get pathnameToEventName() {
    return {
      '/reports': 'Reports Index Page',
      '/reports/stakeholders/new': 'Report Builder Launched',
      '/omni_searches/new': 'Report Builder Launched'
    }
  }

  getReportProperties(reportData) {
    return {
      ...defaultPropertiesReport,
      report_components: reportData.options || null,
      report_format: 'PDF',
      report_id: reportData.id || null,
      report_size: reportData.size || null,
      report_type: this.getReportType(reportData.subtype),
      stakeholders_types: reportData.subtype || null
    }
  }

  getOptionsSelected(formElements) {
    const selectedOptions = []

    for (let i = 0; i < formElements.length; i++) {
      if (formElements[i].nodeName === 'INPUT' && formElements[i].type === 'checkbox' &&
        formElements[i].checked === true) {
        const optionName = formElements[i].name.split('[options]')[1]
        if (optionName !== undefined) {
          selectedOptions.push(optionName.replace('][', '_').replace(/[\[\]']+/g, ''))
        }
      }
    }

    return selectedOptions
  }

  getReportType(reportSubtype) {
    switch (reportSubtype) {
      case 'legislator':
      case 'executive':
      case 'person_contact':
      case 'organization_contact':
      case 'international_legislator':
      case 'committee':
        return 'Stakeholder'
      default:
        return 'Policy'
    }
  }

  getFiltersApplied(arrayOfFilters, filtersObject) {
    const filtersApplied = []

    arrayOfFilters.forEach((filter) => {
      if (this.isFilterApplied(filter, filtersObject)) {
        filtersApplied.push(filter)
      }
    })

    return filtersApplied
  }

  isFilterApplied(filter, filtersObject) {
    const filtersKeys = Object.keys(filtersObject)

    switch (filter) {
      case 'date':
        if (this.isDateAttributePresent(filtersObject)) {
          return true
        }
        break
      case 'source':
      case 'type':
      case 'labels':
      case 'issues':
      case 'location':
      case 'priority':
      case 'position':
      case 'document_type':
      case 'bill_status':
      case 'regulation_agencies':
        const formAttr = this.filterToFormAttr[filter]
        const regex = new RegExp(formAttr) || null

        for (const filter in filtersKeys) {
          if (regex && regex.test(filtersKeys[filter]) && filtersObject[formAttr] !== null) {
            return true
          }
        }
        break
    }
    return false
  }

  isDateAttributePresent(filtersObject) {
    return (
      this.isPresent(filtersObject.date_to) ||
      this.isPresent(filtersObject.date_from) ||
      this.isPresent(filtersObject.start_date) ||
      this.isPresent(filtersObject.end_date)
    )
  }

  get filterToFormAttr() {
    return {
      source: 'export_filter_source',
      type: 'export_filter_type',
      labels: 'labels_ids',
      issues: 'issues_ids',
      location: 'locations',
      priority: 'priorities',
      position: 'positions',
      document_type: 'types',
      bill_status: 'normalized_status',
      regulation_agencies: 'regulation_agencies'
    }
  }
}
